import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { MapPin, CalendarDays, DollarSign, TrendingUp, Plus, Moon, CheckCircle2, X, Hourglass, Inbox, Camera, Star, Award } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import RatingDialog from "@/components/RatingDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_MAP = {
  pending_approval: { label: "En attente", className: "bg-amber-50 text-amber-700 border-amber-200" },
  approved: { label: "Accepté · attente paiement", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { label: "Refusée", className: "bg-red-50 text-red-700 border-red-200" },
  expired: { label: "Expirée", className: "bg-zinc-100 text-zinc-500 border-zinc-200" },
  pending: { label: "Paiement en cours", className: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  confirmed: { label: "Confirmée", className: "bg-green-50 text-green-700 border-green-200" },
  cancelled: { label: "Annulée", className: "bg-red-50 text-red-700 border-red-200" },
  completed: { label: "Terminée", className: "bg-blue-50 text-blue-700 border-blue-200" },
};

function formatDate(d) { try { return format(new Date(d), "d MMM yyyy", { locale: fr }); } catch { return d; } }
function getPhotoUrl(photo) {
  if (!photo) return null;
  if (photo.startsWith("/api/")) return `${BACKEND_URL}${photo}`;
  return photo;
}

function statusBadge(b) {
  // Always show "Terminée" once the stay is done, regardless of stored status
  if (b.is_completed) return STATUS_MAP.completed;
  return STATUS_MAP[b.status] || STATUS_MAP.pending;
}

function HostBookingCard({ booking, index, onApprove, onReject, processing, onRate, highlighted }) {
  const si = statusBadge(booking);
  const isPending = booking.status === "pending_approval";
  const initial = booking.other_user_name?.charAt(0)?.toUpperCase() || "?";
  const photoUrl = getPhotoUrl(booking.other_user_photo);
  const report = booking.checkout_report;
  const reportPhoto = report?.photo_url?.startsWith("/api/") ? `${BACKEND_URL}${report.photo_url}` : report?.photo_url;

  return (
    <Card
      id={`host-booking-${booking.id}`}
      className={`border rounded-2xl bg-white transition-all duration-700 ${highlighted ? "border-emerald-500 ring-2 ring-emerald-300 shadow-lg" : isPending ? "border-amber-200 shadow-sm" : "border-zinc-200"}`}
      data-testid={`host-booking-${index}`}
    >
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="font-heading font-bold text-zinc-950 truncate">{booking.space_title}</h3>
            <p className="text-sm text-zinc-500 flex items-center gap-1 mt-1"><MapPin className="h-3 w-3" /> {booking.space_address || ""}</p>
          </div>
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            <Badge className={`${si.className} border text-xs`} data-testid={`status-badge-${index}`}>{si.label}</Badge>
            {booking.payment_status === "paid" && !booking.is_completed && (
              <Badge className="bg-emerald-600 text-white border-0 text-[10px] gap-1" data-testid={`paid-badge-${index}`}>
                <CheckCircle2 className="h-2.5 w-2.5" /> Payée
              </Badge>
            )}
            {booking.payment_status === "refunded" && (
              <Badge className="bg-zinc-400 text-white border-0 text-[10px]" data-testid={`refunded-badge-${index}`}>
                Remboursée
              </Badge>
            )}
            {booking.payment_status === "partially_refunded" && (
              <Badge className="bg-amber-500 text-white border-0 text-[10px]" data-testid={`partial-refund-badge-${index}`}>
                Partiellement remboursée
              </Badge>
            )}
          </div>
        </div>

        <Link to={`/users/${booking.other_user_id}`} className="block group">
          <div className="flex items-center gap-3 p-3 rounded-xl bg-zinc-50 border border-zinc-100 group-hover:border-emerald-300 group-hover:bg-emerald-50/30 transition-colors">
            <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center overflow-hidden flex-shrink-0">
              {photoUrl ? <img src={photoUrl} alt={booking.other_user_name} className="w-full h-full object-cover" /> : <span className="text-sm font-bold text-emerald-700">{initial}</span>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-semibold text-zinc-950 text-sm truncate">{booking.other_user_name}</span>
                {(booking.other_user_rating_count || 0) > 0 ? (
                  <span className="text-xs text-zinc-500 inline-flex items-center gap-0.5"><Star className="h-3 w-3 text-amber-500 fill-amber-500" /> {(booking.other_user_avg_rating || 0).toFixed(1)} <span className="text-zinc-400">({booking.other_user_rating_count})</span></span>
                ) : <span className="text-[10px] text-zinc-400">Nouveau</span>}
                {booking.other_user_is_superstar && <Badge className="bg-gradient-to-r from-amber-400 to-amber-500 text-white border-0 gap-1 text-[10px] py-0 px-1.5 h-4"><Award className="h-2.5 w-2.5" /> Superstar</Badge>}
              </div>
            </div>
          </div>
        </Link>

        {booking.request_message && isPending && (
          <div className="text-sm text-zinc-700 p-3 rounded-xl bg-emerald-50/50 border border-emerald-100">
            <p className="text-xs font-semibold text-emerald-700 mb-1">Message du campeur :</p>
            <p className="italic">"{booking.request_message}"</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 text-sm text-zinc-600">
          <div className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-zinc-400" /><span>{formatDate(booking.check_in)} - {formatDate(booking.check_out)}</span></div>
          <div className="flex items-center gap-2"><Moon className="h-4 w-4 text-zinc-400" /><span>{booking.total_nights} nuit{booking.total_nights > 1 ? "s" : ""}</span></div>
          {(() => {
            const basePrice = parseFloat(booking.base_price ?? booking.total_price ?? 0);
            const platformFee = parseFloat(booking.platform_fee ?? 0);
            // Match the backend formula exactly. Use stored values once Stripe transferred,
            // otherwise show the estimate so the host sees the *real* amount they will receive.
            const stripeFee = booking.host_transfer_stripe_fee != null
              ? parseFloat(booking.host_transfer_stripe_fee)
              : parseFloat(booking.host_payout_stripe_fee ?? (basePrice > 0 ? 0.30 : 0));
            const payout = booking.host_transfer_amount != null
              ? parseFloat(booking.host_transfer_amount)
              : parseFloat(booking.host_payout_estimate ?? Math.max(basePrice - platformFee - stripeFee, 0));
            if (basePrice <= 0) return null;
            // We fold the fixed Stripe fee into the platform fee line so the
            // breakdown stays simple from the host's perspective.
            const combinedFee = platformFee + stripeFee;
            return (
              <div className="col-span-2 rounded-xl bg-emerald-50/60 border border-emerald-100 p-3 space-y-1 text-xs">
                <div className="flex justify-between text-zinc-600"><span>Prix de la réservation</span><span>{basePrice.toFixed(2)} $</span></div>
                <div className="flex justify-between text-zinc-600"><span>Frais de la plateforme</span><span>−{combinedFee.toFixed(2)} $</span></div>
                <div className="flex justify-between font-bold text-emerald-800 pt-1 border-t border-emerald-200">
                  <span className="inline-flex items-center gap-1"><DollarSign className="h-3.5 w-3.5" /> Vous recevez</span>
                  <span>{payout.toFixed(2)} $</span>
                </div>
              </div>
            );
          })()}
        </div>

        {booking.status === "pending_approval" && booking.expires_at && (
          <p className="text-xs text-amber-700 flex items-center gap-1"><Hourglass className="h-3 w-3" /> Réponse attendue avant {formatDate(booking.expires_at)}</p>
        )}

        {report && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-3 space-y-2" data-testid={`host-checkout-report-${index}`}>
            <div className="flex items-center gap-2 text-xs font-semibold text-emerald-800">
              <Camera className="h-3.5 w-3.5" /> Rapport de départ <span className="text-emerald-700 font-normal">- {report.renter_name}</span>
            </div>
            {reportPhoto && <img src={reportPhoto} alt="État du terrain au départ" className="w-full max-h-56 object-cover rounded-lg" />}
            {report.comment && <p className="text-sm text-zinc-700 italic">"{report.comment}"</p>}
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-1">
          {isPending && (
            <>
              <Button size="sm" className="rounded-xl bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => onApprove(booking)} disabled={processing} data-testid={`approve-btn-${index}`}>
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Accepter
              </Button>
              <Button size="sm" variant="outline" className="rounded-xl text-red-600 border-red-200 hover:bg-red-50" onClick={() => onReject(booking)} disabled={processing} data-testid={`reject-btn-${index}`}>
                <X className="h-3.5 w-3.5 mr-1" /> Refuser
              </Button>
            </>
          )}
          {booking.can_rate && (
            <Button size="sm" className="rounded-xl bg-amber-500 text-white hover:bg-amber-600" onClick={() => onRate(booking)} data-testid={`host-rate-btn-${index}`}>
              <Star className="h-3.5 w-3.5 mr-1" /> Évaluer le campeur
            </Button>
          )}
          {booking.is_completed && booking.rating_submitted && (
            <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 gap-1"><CheckCircle2 className="h-3 w-3" /> Évaluation envoyée</Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialTab = ["pending", "upcoming", "completed"].includes(searchParams.get("tab")) ? searchParams.get("tab") : "pending";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [rejectBooking, setRejectBooking] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [processing, setProcessing] = useState(false);
  const [ratingBooking, setRatingBooking] = useState(null);
  const [highlightId, setHighlightId] = useState(searchParams.get("booking") || null);
  const didScrollRef = useRef(false);

  const load = () => Promise.all([
    api.get("/dashboard/stats").then(r => setStats(r.data)).catch(() => {}),
    api.get("/bookings/received").then(r => setBookings(r.data)).catch(() => {}),
  ]).finally(() => setLoading(false));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Scroll to highlighted booking when navigating from a notification
  useEffect(() => {
    if (didScrollRef.current || loading || !highlightId) return;
    const target = bookings.find(b => b.id === highlightId);
    if (target) {
      let wanted = "pending";
      if (target.is_completed) wanted = "completed";
      else if (["approved", "pending", "confirmed"].includes(target.status)) wanted = "upcoming";
      if (activeTab !== wanted) { setActiveTab(wanted); return; }
    }
    const el = document.getElementById(`host-booking-${highlightId}`);
    if (el) {
      didScrollRef.current = true;
      setTimeout(() => { el.scrollIntoView({ behavior: "smooth", block: "center" }); }, 150);
      const t = setTimeout(() => {
        setHighlightId(null);
        setSearchParams({}, { replace: true });
      }, 4500);
      return () => clearTimeout(t);
    }
  }, [loading, highlightId, activeTab, bookings, setSearchParams]);

  // Once we know the user has 0 spaces, redirect them away — Dashboard is host-only
  useEffect(() => {
    if (!loading && user && (user.spaces_count ?? 0) === 0) {
      navigate("/my-bookings", { replace: true });
    }
  }, [loading, user, navigate]);

  const approve = async (b) => {
    setProcessing(true);
    try { await api.put(`/bookings/${b.id}/approve`); toast.success("Demande acceptée !"); load(); }
    catch (err) {
      const detail = err.response?.data?.detail || "Erreur";
      if (err.response?.status === 403 && /Stripe/i.test(detail)) {
        toast.error(detail, { duration: 8000, action: { label: "Configurer Stripe", onClick: () => navigate("/profile") } });
      } else toast.error(detail);
    } finally { setProcessing(false); }
  };

  const submitReject = async () => {
    if (!rejectBooking) return;
    setProcessing(true);
    try {
      await api.put(`/bookings/${rejectBooking.id}/reject`, { reason: rejectReason });
      toast.success("Demande refusée");
      setRejectBooking(null); setRejectReason("");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setProcessing(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-zinc-300 border-t-emerald-700 rounded-full" /></div>;

  const statCards = [
    { label: "Mes terrains", value: stats?.total_spaces || 0, icon: MapPin, color: "text-emerald-600 bg-emerald-600/10" },
    { label: "Réservations actives", value: stats?.active_bookings || 0, icon: CalendarDays, color: "text-green-600 bg-green-600/10" },
    { label: "Total réservations", value: stats?.total_bookings || 0, icon: TrendingUp, color: "text-violet-600 bg-violet-600/10" },
    { label: "Revenus", value: `${(stats?.total_revenue || 0).toFixed(2)} $`, icon: DollarSign, color: "text-amber-600 bg-amber-600/10" },
  ];

  // Buckets
  const pendingRequests = bookings.filter(b => b.status === "pending_approval");
  const upcoming = bookings.filter(b => !b.is_completed && ["approved", "pending", "confirmed"].includes(b.status));
  const completed = bookings.filter(b => b.is_completed);
  const other = bookings.filter(b => !pendingRequests.includes(b) && !upcoming.includes(b) && !completed.includes(b));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in">
      <div className="flex items-center justify-between mb-8 gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950" data-testid="dashboard-title">Tableau de bord</h1>
          <p className="text-sm text-zinc-500 mt-1">Suivez vos demandes, vos séjours à venir et l'historique de vos locations.</p>
        </div>
        <Link to="/create-space" data-testid="dashboard-add-space"><Button className="bg-emerald-700 text-white hover:bg-emerald-800 rounded-xl"><Plus className="h-4 w-4 mr-2" /> Publier un terrain</Button></Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((stat, i) => (
          <Card key={i} className="border border-zinc-200 rounded-2xl bg-white" data-testid={`stat-card-${i}`}>
            <CardContent className="p-5">
              <div className={`h-10 w-10 rounded-xl flex items-center justify-center mb-3 ${stat.color}`}><stat.icon className="h-5 w-5" /></div>
              <p className="text-2xl font-bold text-zinc-950 font-heading">{stat.value}</p>
              <p className="text-sm text-zinc-500 mt-1">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border border-zinc-200 rounded-2xl bg-white">
        <CardHeader>
          <CardTitle className="font-heading text-xl">Mes locations</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6 bg-zinc-100 rounded-xl p-1">
              <TabsTrigger value="pending" className="rounded-lg gap-2" data-testid="tab-host-pending">
                <Inbox className="h-3.5 w-3.5" /> Demandes ({pendingRequests.length})
                {pendingRequests.length > 0 && <Badge className="bg-amber-500 text-white border-0 text-[10px] h-4 px-1.5">{pendingRequests.length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="upcoming" className="rounded-lg gap-2" data-testid="tab-host-upcoming">
                <CalendarDays className="h-3.5 w-3.5" /> À venir ({upcoming.length})
              </TabsTrigger>
              <TabsTrigger value="completed" className="rounded-lg gap-2" data-testid="tab-host-completed">
                <CheckCircle2 className="h-3.5 w-3.5" /> Terminées ({completed.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="pending">
              {pendingRequests.length === 0 ? (
                <div className="text-center py-12" data-testid="no-pending"><Inbox className="h-10 w-10 text-zinc-300 mx-auto mb-3" /><p className="text-sm text-zinc-500">Aucune demande en attente</p></div>
              ) : (
                <div className="space-y-4">{pendingRequests.map((b, i) => <HostBookingCard key={b.id} booking={b} index={i} onApprove={approve} onReject={setRejectBooking} processing={processing} onRate={setRatingBooking} highlighted={highlightId === b.id} />)}</div>
              )}
            </TabsContent>

            <TabsContent value="upcoming">
              {upcoming.length === 0 ? (
                <div className="text-center py-12" data-testid="no-upcoming"><CalendarDays className="h-10 w-10 text-zinc-300 mx-auto mb-3" /><p className="text-sm text-zinc-500">Aucune réservation à venir</p></div>
              ) : (
                <div className="space-y-4">{upcoming.map((b, i) => <HostBookingCard key={b.id} booking={b} index={i} onApprove={approve} onReject={setRejectBooking} processing={processing} onRate={setRatingBooking} highlighted={highlightId === b.id} />)}</div>
              )}
            </TabsContent>

            <TabsContent value="completed">
              {completed.length === 0 ? (
                <div className="text-center py-12" data-testid="no-completed"><CheckCircle2 className="h-10 w-10 text-zinc-300 mx-auto mb-3" /><p className="text-sm text-zinc-500">Aucun séjour terminé pour le moment</p></div>
              ) : (
                <div className="space-y-4">{completed.map((b, i) => <HostBookingCard key={b.id} booking={b} index={i} onApprove={approve} onReject={setRejectBooking} processing={processing} onRate={setRatingBooking} highlighted={highlightId === b.id} />)}</div>
              )}
              {other.length > 0 && (
                <div className="mt-8">
                  <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-3">Autres ({other.length})</p>
                  <div className="space-y-4">{other.map((b, i) => <HostBookingCard key={b.id} booking={b} index={i} onApprove={approve} onReject={setRejectBooking} processing={processing} onRate={setRatingBooking} highlighted={highlightId === b.id} />)}</div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <RatingDialog booking={ratingBooking} open={!!ratingBooking} onOpenChange={(v) => !v && setRatingBooking(null)} onSubmitted={load} />

      <Dialog open={!!rejectBooking} onOpenChange={(v) => { if (!v) { setRejectBooking(null); setRejectReason(""); } }}>
        <DialogContent className="rounded-2xl" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Refuser la demande</DialogTitle>
            <DialogDescription>Indiquez une raison (optionnel). Le campeur sera notifié.</DialogDescription>
          </DialogHeader>
          <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Ex : Dates indisponibles, terrain en entretien..." className="rounded-xl min-h-[80px]" maxLength={300} data-testid="reject-reason-input" />
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setRejectBooking(null)}>Annuler</Button>
            <Button className="bg-red-600 text-white hover:bg-red-700 rounded-xl" onClick={submitReject} disabled={processing} data-testid="reject-confirm-btn">{processing ? "Envoi..." : "Refuser"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
